import React from 'react';
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from '@react-pdf/renderer';
import { format } from 'date-fns';

// Create styles
const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    padding: 40,
    paddingBottom: 50,
    fontSize: 10,
    fontFamily: 'Helvetica',
  },
  header: {
    marginBottom: 12,
    borderBottom: '2 solid #f97316',
    paddingBottom: 10,
    textAlign: 'center',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1f2937',
    marginBottom: 5,
  },
  headerSubtitle: {
    fontSize: 11,
    color: '#6b7280',
  },
  contractNumber: {
    fontSize: 9,
    color: '#9ca3af',
    marginTop: 8,
  },
  introText: {
    fontSize: 10,
    color: '#1f2937',
    lineHeight: 1.4,
    marginBottom: 8,
    textAlign: 'justify',
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#f97316',
    marginBottom: 4,
    marginTop: 8,
    borderBottom: '1 solid #e5e7eb',
    paddingBottom: 3,
  },
  termsText: {
    fontSize: 9,
    color: '#374151',
    lineHeight: 1.4,
    marginBottom: 3,
    textAlign: 'justify',
  },
  bulletText: {
    fontSize: 9,
    color: '#374151',
    lineHeight: 1.4,
    marginBottom: 2,
    marginLeft: 10,
  },
  boldText: {
    fontWeight: 'bold',
  },
  paymentBox: {
    backgroundColor: '#fef3c7',
    padding: 8,
    borderRadius: 4,
    marginTop: 4,
    marginBottom: 4,
  },
  paymentRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 3,
  },
  paymentLabel: {
    fontSize: 9,
    color: '#4b5563',
  },
  paymentValue: {
    fontSize: 9,
    color: '#1f2937',
    fontWeight: 'bold',
  },
  signatureSection: {
    marginTop: 10,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  signatureBlock: {
    width: '45%',
  },
  signatureLine: {
    borderBottom: '1 solid #1f2937',
    marginBottom: 6,
    paddingBottom: 25,
  },
  signatureLabel: {
    fontSize: 9,
    color: '#6b7280',
    marginBottom: 2,
  },
  signatureImage: {
    height: 50,
    objectFit: 'contain',
    marginBottom: 6,
  },
  footer: {
    textAlign: 'center',
    color: '#9ca3af',
    fontSize: 8,
    borderTop: '1 solid #e5e7eb',
    paddingTop: 8,
    marginTop: 'auto',
  },
  divider: {
    borderBottom: '1 solid #e5e7eb',
    marginTop: 6,
    marginBottom: 6,
  },
  agreementText: {
    fontSize: 9,
    color: '#374151',
    lineHeight: 1.4,
    marginTop: 6,
    marginBottom: 4,
    fontStyle: 'italic',
  },
  addendumSection: {
    marginTop: 6,
    padding: 8,
    backgroundColor: '#f9fafb',
    borderRadius: 4,
    borderLeft: '3 solid #f97316',
  },
  addendumTitle: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#f97316',
    marginBottom: 6,
  },
  addendumText: {
    fontSize: 9,
    color: '#374151',
    lineHeight: 1.4,
  },
  eSignedText: {
    fontSize: 8,
    color: '#6b7280',
    textAlign: 'center',
    marginTop: 12,
    fontStyle: 'italic',
  },
});

// Format currency
const formatCurrency = (amount) => {
  return `$${Number(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
};

// Format date safely — parse as local time to avoid UTC timezone off-by-one
const formatDate = (dateStr) => {
  if (!dateStr) return 'TBD';
  try {
    // YYYY-MM-DD from date input must be parsed as local, not UTC
    if (/^\d{4}-\d{2}-\d{2}$/.test(String(dateStr))) {
      const [year, month, day] = String(dateStr).split('-').map(Number);
      return format(new Date(year, month - 1, day), 'MMMM d, yyyy');
    }
    return format(new Date(dateStr), 'MMMM d, yyyy');
  } catch {
    return dateStr;
  }
};

// Format time (convert 24h to 12h format)
const formatTime = (timeStr) => {
  if (!timeStr) return 'TBD';
  if (/AM|PM/i.test(timeStr)) return timeStr; // already 12h, avoid double AM/PM
  try {
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  } catch {
    return timeStr;
  }
};

// Contract PDF Document Component
export default function ContractTemplate({ contract, signatureUrl = null }) {
  const eventDate = formatDate(contract.eventDate);
  const eventTime = formatTime(contract.eventTime);
  const eventEndTime = formatTime(contract.eventEndTime);
  const loadInTime = formatTime(contract.loadInTime);
  const soundCheckTime = formatTime(contract.soundCheckTime);
  const remainingBalance = (contract.totalAmount || 0) - (contract.depositAmount || 0) - (contract.secondPaymentAmount || 0);

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.headerTitle}>Sirius Enterprise Contract</Text>
          <Text style={styles.headerSubtitle}>
            {'"'}Scooby DaGodSon Featuring Sirius Company{'"'}
          </Text>
          <Text style={styles.contractNumber}>
            Contract #: {contract.id?.substring(0, 8).toUpperCase() || 'DRAFT'}
          </Text>
        </View>

        {/* Introduction */}
        <Text style={styles.introText}>
          This Performance Agreement (hereinafter referred to as {'"'}Agreement{'"'}), dated for {eventDate}, is made by and between Sirius Enterprise (hereinafter referred to as {'"'}Performers{'"'}) and {contract.clientName || '_______________________________________'} (hereinafter referred to as {'"'}Purchaser{'"'}). It is mutually agreed by and between the parties that the Purchaser hereby engages the Performers, and the Performers hereby agree to perform the Engagement (hereinafter {'"'}the Performance{'"'}) subject to all of the terms and conditions set forth on the face of this Agreement.
        </Text>

        {/* DATE/TIME/LOCATION */}
        <Text style={styles.sectionTitle}>DATE/TIME/LOCATION</Text>
        <Text style={styles.termsText}>
          The Purchaser shall host an event on {eventDate}, at Venue named {contract.venue || '__________________________'}. The Performers will provide live musical entertainment between the hours of {eventTime} and {eventEndTime}, totaling {contract.packageDuration || '2 SETS (45 min each) or 1 SET (90 mins)'}.
        </Text>
        {contract.venueAddress && (
          <Text style={styles.termsText}>
            Venue Address: {contract.venueAddress}
          </Text>
        )}

        {/* COMPENSATION */}
        <Text style={styles.sectionTitle}>COMPENSATION</Text>
        <Text style={styles.termsText}>
          The Purchaser shall pay the Performers a total fee of {formatCurrency(contract.totalAmount)} for the Performance, payable as follows: (1) a non refundable deposit of {formatCurrency(contract.depositAmount)} due upon execution of this Agreement in order to execute and secure the date;{contract.secondPaymentAmount ? ` (2) a second payment of ${formatCurrency(contract.secondPaymentAmount)}${contract.secondPaymentDue ? ` ${contract.secondPaymentDue}` : ''}; and (3)` : ' and (2)'} the remaining balance of {formatCurrency(remainingBalance)} due on or before the day of the Performance. Final payment is required prior to the band taking the stage.
        </Text>
        <Text style={styles.termsText}>
          Deposits and payments may be made via Cash, Zelle, CashApp, or secure payment link via Stripe. For payments made via Stripe, applicable processing fees will apply. Payment details are as follows: CashApp: $siriuscompanydc and Zelle: franksiriusmail@gmail.com
        </Text>
        <Text style={styles.termsText}>
          Failure to make any payment when due shall constitute a material breach of this Agreement.
        </Text>

        {/* TRAVEL REQUIREMENTS */}
        <Text style={styles.sectionTitle}>TRAVEL REQUIREMENTS</Text>
        <Text style={styles.termsText}>
          All travel, if applicable, MUST be booked through Performers Travel Agency, unless Purchaser has competitive rates that are verified and approved by Performer. If travel is being provided, the following must be included: Direct non stop flight for all performers, sleeping accommodations at Hotels rated 4-Star or above and professional ground transportation to and from venue and airport.
        </Text>

        {/* ARTWORK AND ADVERTISING APPROVAL */}
        <Text style={styles.sectionTitle}>ARTWORK AND ADVERTISING APPROVAL</Text>
        <Text style={styles.termsText}>
          ALL artwork, flyers, and advertisements must be approved by Performer/Management prior to any electronic or physical distribution and may not be distributed prior to payment of deposit. All advertising materials must contain the Performers logo and pictures as provided by the Performers.
        </Text>

        {/* CANCELLATION/BREACH/FORCE MAJEURE */}
        <Text style={styles.sectionTitle}>CANCELLATION/BREACH/FORCE MAJEURE</Text>
        <Text style={styles.termsText}>
          If the Purchaser fails to comply with any material term or financial obligation under this Agreement, the Performer reserves the right to cancel the Performance upon written notice. In the event of such cancellation due to Purchaser default, any deposits paid shall be forfeited and retained by the Performer. All deposits are non-refundable and become fully non-refundable within ninety (90) calendar days prior to the scheduled event date, as the Performer commits the date exclusively and may decline other opportunities.
        </Text>
        <Text style={styles.termsText}>
          If the Performers have completed one (1) set of the live performance and the Performance is thereafter canceled, interrupted, or terminated for any reason other than Performer's willful misconduct, Purchaser shall pay the Performers one hundred percent (100%) of the total contracted fee.
        </Text>
        <Text style={styles.termsText}>
          If the Performers' obligations are delayed or prevented due to acts of God, strikes or other labor disputes, accidents, injuries, illness, transportation malfunctions, weather conditions, fire, acts of war or terrorism, force majeure events, or any other cause beyond the reasonable control of the Performers, the Performance shall be rescheduled, upon mutual agreement of Purchaser and Performer, to the first available date. In such event, all deposits shall be applied to the rescheduled Performance and shall remain non-refundable.
        </Text>

        {/* INDEMNITY */}
        <Text style={styles.sectionTitle}>INDEMNITY</Text>
        <Text style={styles.termsText}>
          Purchaser shall indemnify and hold Performer harmless from any and all costs and expenses (including reasonable actual legal fees) arising out of: (i) any claims and/or causes of actions or costs of any kind, asserted by any third party against Performer with respect to Performer's performance at the Event not caused by Performer's gross negligence, and/or (ii) breach of any of Purchaser's responsibilities, representations and/or obligations as set forth on the front and reverse of this Agreement.
        </Text>

        {/* LICENSES/INSURANCE/SECURITY */}
        <Text style={styles.sectionTitle}>LICENSES/INSURANCE/SECURITY</Text>
        <Text style={styles.termsText}>
          At Purchaser's expense, Purchaser shall obtain any and all required licenses and permits for the Performance and shall obtain and have in place all reasonable and necessary fire, liability and other insurance, as may be appropriate and/or required by law for the Performance as well as adequate security to protect Performer, Performer's property and equipment, and all persons in attendance.
        </Text>

        {/* MERCHANDISE */}
        <Text style={styles.sectionTitle}>MERCHANDISE</Text>
        <Text style={styles.termsText}>
          Performer shall have sole and exclusive right but not the obligation to sell souvenir merchandise and programs including audio and audiovisual recordings and the receipts thereof shall belong solely to Performer.
        </Text>

        {/* SOUNDCHECK/CONTROL OF PRODUCTION */}
        <Text style={styles.sectionTitle}>SOUNDCHECK/CONTROL OF PRODUCTION</Text>
        <Text style={styles.termsText}>
          Performer shall be provided reasonable time for sound check and rehearsal at venue on date of Performance and Performer shall have sole and exclusive control over all aspects of production, presentation, and routine of Performer's show for Performance, including but not limited to personnel, instrumentation and repertoire.
        </Text>

        {/* SOUND/LIGHTING/STAGE REQUIREMENTS */}
        <Text style={styles.sectionTitle}>SOUND/LIGHTING/STAGE REQUIREMENTS/ADDITIONAL CONTACTS</Text>
        <Text style={styles.termsText}>
          NO LIMIT Sound Company EXCLUSIVELY holds the Rider and Stage Plot for Performer and must be used by the Purchaser for ALL events. The ONLY exceptions to this requirement are if the venue has a house system that is deemed adequate and approved by the Performer. The Purchaser MUST also provide, at no cost to the Performer, adequate staging and lights. Staging and lights MUST be approved by Performers prior to Performance.
        </Text>
        <Text style={styles.termsText}>
          Load In/Sound Check Time: {loadInTime}
        </Text>
        <Text style={styles.termsText}>
          Sound check MUST be completed by: {soundCheckTime}
        </Text>

        {/* ADDITIONAL REQUIREMENTS FOR PERFORMERS BY CLIENT */}
        <Text style={styles.sectionTitle}>ADDITIONAL REQUIREMENTS FOR PERFORMERS BY CLIENT</Text>
        <Text style={styles.bulletText}>• Minimum two (2) hours of setup prior to start of Performance and Purchaser shall inform Performers of the earliest available time that setup can begin.</Text>
        <Text style={styles.bulletText}>• Food if provided by caterer, sodas and/or bottled water.</Text>
        <Text style={styles.bulletText}>• A Green Room/Dressing Room.</Text>
        <Text style={styles.bulletText}>• Parking for Performers (at least 5). If venue has a parking lot.</Text>
        <Text style={styles.bulletText}>• An area for Performers to unload and/or park vehicles.</Text>
        <Text style={styles.bulletText}>• Performers are allowed at least 1 guest each, not to exceed number of band members/management.</Text>

        <View style={styles.divider} />

        {/* Special Addendum */}
        {contract.addendum && (
          <View style={styles.addendumSection}>
            <Text style={styles.addendumTitle}>SPECIAL ADDENDUM</Text>
            <Text style={styles.addendumText}>{contract.addendum}</Text>
          </View>
        )}

        {/* Agreement Text */}
        <Text style={styles.agreementText}>
          The undersigned parties have read and agreed to all of the terms and conditions of this Agreement including of any {'"'}Additional Terms and Conditions{'"'} on the attached which are deemed incorporated herein and made a part hereof and the provisions of any annexed Performance rider.
        </Text>

        {/* Signature Section */}
        <View style={styles.signatureSection}>
          <View style={styles.signatureBlock}>
            {signatureUrl ? (
              <Image src={signatureUrl} style={styles.signatureImage} />
            ) : (
              <View style={styles.signatureLine} />
            )}
            <Text style={styles.signatureLabel}>PURCHASER</Text>
            <Text style={styles.signatureLabel}>{contract.clientName || '_______________'}</Text>
            <Text style={styles.signatureLabel}>Date: {signatureUrl ? formatDate(new Date()) : '_______________'}</Text>
          </View>
          <View style={styles.signatureBlock}>
            <View style={styles.signatureLine} />
            <Text style={styles.signatureLabel}>AUTHORIZED BOOKING AGENT</Text>
            <Text style={styles.signatureLabel}>Date: _______________</Text>
          </View>
        </View>

        {/* E-Signed Notice */}
        <Text style={styles.eSignedText}>
          This contract has been electronically signed and is legally binding under the Electronic Signatures in Global and National Commerce (E-SIGN) Act.
        </Text>

        {/* Footer */}
        <Text style={styles.footer} render={({ pageNumber, totalPages }) => (
          `Scooby DaGodSon | Sirius Company | DMV Go-Go Entertainment  —  Page ${pageNumber} of ${totalPages}`
        )} fixed />
      </Page>
    </Document>
  );
}
